/**
 * Giriş kapısı (login surface) — iç kullanıcı mı iş ortağı mı?
 *
 * İki ayrı giriş EKRANI var ama TEK kimlik doğrulama var (`/auth/login`).
 * Sunucu `surface` alanıyla kapıyı tanır: partner kapısı yalnız PARTNER
 * hesabını, iç kapı yalnız iç hesabı kabul eder (15.10.2026). Buradaki kod
 * ise yalnız sunum ve "oturum bitince nereye dönülecek" içindir:
 * iş ortağı `/partner/login`'den girdiyse token düşünce iç VERTEX ekranına
 * değil kendi kapısına dönmeli — yoksa "yanlış yere geldim" hissi doğar ve
 * partner iç uygulamanın giriş ekranını görür.
 *
 * ⚠️ Bu bir GÜVENLİK katmanı DEĞİLDİR. Kim neyi görebilir kararı sunucudadır
 * (`main.py:partner_route_guard` + RBAC). Burası yalnız yönlendirme tercihidir;
 * localStorage silinirse en kötü ihtimalle iç giriş ekranına düşülür.
 */

const KEY = 'vertex.loginSurface'

export const SURFACE_INTERNAL = 'internal'
export const SURFACE_PARTNER = 'partner'

export function setLoginSurface(surface) {
  try {
    if (surface === SURFACE_PARTNER) localStorage.setItem(KEY, SURFACE_PARTNER)
    else localStorage.removeItem(KEY)
  } catch {
    /* private mode / kota — yönlendirme varsayılana düşer, akış kırılmaz */
  }
}

export function getLoginSurface() {
  try {
    return localStorage.getItem(KEY) === SURFACE_PARTNER ? SURFACE_PARTNER : SURFACE_INTERNAL
  } catch {
    return SURFACE_INTERNAL
  }
}

/**
 * Çıkıştan ÖNCE çağrılır: dönülecek kapıyı hesabın türünden belirler.
 * Yalnız "son girilen kapı" işaretine bakmak yetmiyordu — işaret yoksa (başka
 * tarayıcı sekmesi, temizlenmiş depolama) iş ortağı iç /login ekranına düşüyordu.
 */
export function prepareLogout(user) {
  setLoginSurface(user?.role === 'PARTNER' ? SURFACE_PARTNER : null)
}

/** Oturum bitince / 401'de gidilecek giriş ekranı. */
export function loginPath() {
  return getLoginSurface() === SURFACE_PARTNER ? '/partner/login' : '/login'
}

/**
 * Giriş sonrası hedef — TEK kaynak. İki giriş ekranı ve App.jsx'teki
 * PartnerGuard aynı fonksiyonu kullanır; ayrışırsa biri partneri var olmayan
 * (yetkisi kapatılmış) bir sayfaya atar ve kullanıcı boş ekran görür.
 */
export function homePathForUser(user) {
  const perms = user?.permissions || {}
  const inScope = (mod, axis = 'VIEW') => {
    const p = perms[mod] || {}
    // Yetki matrisi bazı yollarda 'VIEW', bazılarında 'View' anahtarıyla gelir
    const sc = String(p[axis] ?? p[axis[0] + axis.slice(1).toLowerCase()] ?? '').toUpperCase()
    return ['OWN', 'TEAM', 'ALL'].includes(sc)
  }
  if (user?.role !== 'PARTNER') return '/dashboard'

  // Panel özeti her zaman açıktır (modülsüz rota) — tek tek sayfalara düşmek
  // yerine oraya göndermek, izinleri kısıtlı partnerin boş ekranla
  // karşılaşmasını önler.
  const anyPanel = ['PARTNER.CATALOG', 'PARTNER.ORDERS', 'PARTNER.STOCK_OWN',
                    'PARTNER.INVOICES', 'PARTNER.SHIPMENTS']
  if (anyPanel.some((m) => inScope(m))) return '/partner'
  if (inScope('STOCK.STATUS')) return '/stock/status'
  return '/settings/profile'
}

/**
 * PARTNER rolünün girebildiği sayfa yolları — TEK kaynak.
 *
 * `App.jsx`'teki iki guard (PartnerGuard + ProtectedRoute) ve `Sidebar`
 * aynı listeyi okur. Ayrı ayrı yazıldığında menü bir sayfayı gösterip guard
 * onu panele geri atıyordu ("Stok › Ürün Kataloğu açılmıyor", 06.10.2026):
 * PARTNER rolünün varsayılanlarına STOCK.PRODUCTS/MOVEMENTS gibi iç modül
 * anahtarları sızdığı için menü modül yetkisine bakıp sayfayı listeliyordu.
 * Menü artık yetkiye EK olarak bu listeye de bakar.
 *
 * ⚠️ Güvenlik katmanı değildir — API tarafı `main.py:partner_route_guard`.
 */
export const PARTNER_PATH_PREFIXES = [
  '/partner',               // panel + alt sayfalar
  '/stock/status',          // bizim anlık stok (bilinçli açık)
  '/notifications',         // bildirim akışı (zil buraya gider)
  '/settings/security',
  '/settings/profile',
  '/settings/notifications',
]

export function partnerPathAllowed(path) {
  const p = String(path || '')
  return PARTNER_PATH_PREFIXES.some((pre) => p === pre || p.startsWith(pre + '/'))
}
